//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import { connect } from "react-redux";
// suggestion-card imports
import { IProps, IContainer, IState } from "./SuggestionCard.d";
// other components
import { asCard, IRemoveCallback } from "@components/card";
import { SuggestionAction } from "@components/suggestion-action";
import { SuggestionBody } from "@components/suggestion-body";
import { SuggestionCardHeader } from "@components/suggestion-card-header";
// state
import { IReduxState } from "@reducers/root.reducer";
import { track, TrackingEvents } from "@util/analytics.util";
// util
import { EPayloadType } from "@util/suggestion.util";

//////////////////
// COMPONENT
//////////////////
class SuggestionCardComponent extends React.Component<IProps, IState> {
  // ~-~-~-~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);
    this.state = {};
    this.maybeTrackShown(props);
  }

  private maybeTrackShown = (props: IProps) => {
    if (!props.feedSuggestion) {
      return;
    }
    const trackingData = this.getTrackingData(props.feedSuggestion);
    track(TrackingEvents.SUGGESTIONS.SHOWN, trackingData);
  };

  // ~-~-~-~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-~-~-~-
  // pull tracking data off feed suggestion and format
  private getTrackingData = (feedSuggestion: ISchema.IFeedSuggestion) => {
    const { suggestionType, suggestionId } = feedSuggestion;
    const cardType = this.getTrackingCardType(feedSuggestion);
    return { cardType, suggestionType, suggestionId };
  };

  // need additional tracking data for tip/config card types
  private getTrackingCardType = (feedSuggestion: ISchema.IFeedSuggestion) => {
    let cardType: string = feedSuggestion.primaryAction.type;
    if (feedSuggestion.suggestionPayload.__typename === EPayloadType.Tip) {
      cardType = feedSuggestion.suggestionPayload.tipType;
    } else if (feedSuggestion.suggestionPayload.__typename === EPayloadType.Config) {
      cardType = feedSuggestion.suggestionPayload.configurationType;
    }
    return cardType;
  };

  // passing autopilot stuff from action to body... sigh
  private updateAutopilotOptions = (autopilotOptions: IState["autopilotOptions"]) => {
    this.setState({ autopilotOptions });
  };

  // ~-~-~-~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-~-~-~-
  // add some tracking data to every  dismiss / action
  private onCallRemove: IRemoveCallback = (args, passedTrackingData) => {
    const { removeCard, feedSuggestion } = this.props;
    if (!removeCard || !feedSuggestion) {
      return;
    }
    const trackingData = this.getTrackingData(feedSuggestion);
    removeCard(args, { ...passedTrackingData, ...trackingData });
  };

  // on clicking the dismiss x in the corner
  private onClickDismiss = () => {
    const markSeenFeedSuggestionInput = { feedSuggestionRelayId: this.props.feedSuggestion.id };
    this.onCallRemove({ markSeenFeedSuggestionInput });
  };

  // ~-~-~-~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-~-~-~-
  public render() {
    const { feedSuggestion, org } = this.props;

    return (
      <div>
        <SuggestionCardHeader
          feedSuggestion={feedSuggestion}
          removeCard={this.onClickDismiss}
          org={org}
        />
        <SuggestionBody
          autopilotOptions={this.state.autopilotOptions}
          suggestionPayload={feedSuggestion.suggestionPayload}
          org={org}
        />
        <SuggestionAction
          autopilotOptionsUpdate={(opts: IState["autopilotOptions"]) =>
            this.updateAutopilotOptions(opts)
          }
          feedSuggestion={feedSuggestion}
          actionCallback={this.onCallRemove}
          org={org}
        />
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {})(SuggestionCardComponent);

//////////////////
// RELAY
//////////////////
import { fragments } from "./SuggestionCard.gql";
export const SuggestionCard = createFragmentContainer<IContainer>(
  asCard<IContainer>(reduxContainer),
  fragments,
);
